import { useCallback } from 'react';

import { gql } from '@apollo/client';
import {
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client/react';
import {
  AgentDelegationTaskType,
  CreateInboxEntryInput,
  InboxEntry,
  InboxEntryCollectionMutations,
  InboxEntryFilters,
  InboxEntryOrderKeyAssignment,
  InboxEntrySort,
  LocalCodingAgentRuntime,
  UnreadInboxEntriesByTaskGroup,
  UpdateInboxEntryInput,
} from '@this+that/models';
import log from 'loglevel';

import type { ExecutorApprovalStep } from './agentSetup.service';
import { buildAbortableQueryContext } from './buildAbortableQueryContext';
import { createApiWithAuth } from '../utils/axios';
import { FETCH_POLICY_CACHE_AND_NETWORK } from '../utils/constants';

export const AGENT_DELEGATION_FIELDS = gql`
  fragment AgentDelegationFields on AgentDelegation {
    delegationId
    kind
    status
    taskType
    localRuntime
    localModel
    localAgentProfile
    delegatedByUserId
    targetName
    mcpConnectionId
    jobId
    conversationId
    runId
    agentTemplateId
    skipApprovals
    statusMessage
    activity
    error
    errorCode
    logTailExcerpt
    executionLog {
      at
      kind
      status
      message
      attempt
    }
    pendingQuestion {
      text
      options
      askedAt
      isApproval
    }
    pendingApproval {
      message
      actions {
        id
        label
        toolName
        effect {
          recipients
          recipientCount
          target
          subject
          bodyPreview
          attachmentNames
          attachmentCount
          workspace
        }
      }
    }
    ledgerRef {
      conversationId
      runId
      msgIdx
    }
    blockedReason
    endedReason
    answeredInTextOnly
    prUrl
    plan
    # planHash is derived, never persisted: selecting any other field the API does not
    # expose in this fragment fails the whole query (WEB-P0.04).
    planHash
    prState
    prNumber
    prChecksStatus
    prChecksFailedCount
    prReviewDecision
    prReviewCommentCount
    prSignalsUpdatedAt
    startedAt
    finishedAt
  }
`;

// The API ships before the web build that selects `agentRuns` and `ticketArtifacts`: an API that
// predates them rejects any query naming them. Selected on every entry fragment, mutation
// responses included, because handleUpdateInboxEntry replaces the stored entry with the response.
// `repositorySlug` is sealed content the API sends only to the user who delegated the run.
export const TICKET_ARTIFACT_FIELDS = gql`
  fragment TicketArtifactFields on TicketArtifact {
    id
    createdAt
    updatedAt
    sourceRunId
    pullRequest {
      url
      repositorySlug
      number
      state
      checksStatus
      checksFailedCount
      reviewDecision
      reviewCommentCount
      signalsUpdatedAt
    }
  }
`;

// handleUpdateInboxEntry replaces the stored entry with whatever a mutation returns and
// keeps only communications, relatedInboxEntries, newReplies, chatActivity and
// taskSubgroupId from the old one, so every InboxEntry response selects at least these
// fields or the store blanks them.
const INBOX_ENTRY_CORE_FIELDS = gql`
  fragment InboxEntryCoreFields on InboxEntry {
    id
    createdAt
    createdFor
    subject
    description
    participantIds
    isSuggestion
    taskGroupId
    taskSubgroupId
    orderKey
    taskLabelIds
    relatedInboxEntryIds
    version
    deletedAt
    communicationIds
    dueAt
    resolvedAt
    resolutionSuggestedAt
    approvalDecision
    updatedAt
    priority
    entryKind
    executorAttention
    executorAttentionReasons
    flowApproval {
      executionId
      flowId
      ownerUserId
      pauseStepId
    }
    agentDelegation {
      ...AgentDelegationFields
    }
    agentRuns {
      ...AgentDelegationFields
    }
    ticketArtifacts {
      ...TicketArtifactFields
    }
    checklists {
      id
      title
      subtasks {
        id
        title
        completed
      }
    }
  }
  ${AGENT_DELEGATION_FIELDS}
  ${TICKET_ARTIFACT_FIELDS}
`;

const INBOX_ENTRY_COMMUNICATION_FIELDS = gql`
  fragment InboxEntryCommunicationFields on UserCommunication {
    messageId
    userId
    createdAt
    author
    userEmailAddress
    authorUserId
    authorEmail
    authorAvatarUrl
    messagePreview
    messageRawText
    messageText
    channel
    channelName
    channelType
    threadMessageId
    isThreadReply
    threadTimestamp
    recipients {
      userId
      email
      name
      slackUserId
      avatarUrl
      type
    }
    subject
    platform
    messageUrl
    labelIds
    category
    s3Key
    inboxEntryIds
    draftId
    slackWorkspaceId
    slackThreadTimestamp
    calendarEventData {
      eventId
      icalUid
      responseStatus
      summary
      organizer {
        name
        email
      }
      eventTime {
        startTime
        endTime
        timeZone
      }
    }
    threadMessages {
      userId
      messageId
      userEmailAddress
      createdAt
      author
      authorUserId
      authorEmail
      authorAvatarUrl
      messagePreview
      channel
      channelName
      threadMessageId
      isThreadReply
      threadTimestamp
      recipients {
        userId
        email
        name
        slackUserId
        avatarUrl
        type
      }
      subject
      platform
      messageUrl
      labelIds
      category
      s3Key
      inboxEntryIds
      draftId
    }
  }
`;

// Detail query only. An API that predates `agentRunHistory` rejects any query selecting it,
// so the API ships before the web build that carries this fragment.
export const AGENT_RUN_SUMMARY_FIELDS = gql`
  fragment AgentRunSummaryFields on AgentRunSummary {
    id
    kind
    taskType
    targetName
    agentTemplateId
    localRuntime
    localModel
    localAgentProfile
    delegatedByUserId
    status
    blockedReason
    endedReason
    answeredInTextOnly
    endReason
    startedAt
    finishedAt
    archivedAt
    prUrl
    prNumber
    prState
    prChecksStatus
    prChecksFailedCount
    prReviewDecision
    prReviewCommentCount
    prSignalsUpdatedAt
    errorCode
    statusMessage
    error
    executionLog {
      at
      kind
      status
      message
      attempt
    }
    runId
    conversationId
    ledgerConversationId
    mcpConnectionId
    jobId
  }
`;

export const INBOX_ENTRY_FIELDS = gql`
  fragment InboxEntryFields on InboxEntry {
    ...InboxEntryCoreFields
    participants {
      id
      name
      email
      title
    }
    communications {
      ...InboxEntryCommunicationFields
    }
    newReplies {
      unitKey
      messageIds
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
  ${INBOX_ENTRY_COMMUNICATION_FIELDS}
`;

const GET_PAGINATED_INBOX_ENTRIES = gql`
  query getPaginatedInboxEntries(
    $taskGroupId: String!
    $limit: Int!
    $exclusiveStartKey: String
    $filters: InboxEntryFiltersInput
    $sort: InboxEntrySortInput
  ) {
    getPaginatedInboxEntries(
      taskGroupId: $taskGroupId
      limit: $limit
      exclusiveStartKey: $exclusiveStartKey
      filters: $filters
      sort: $sort
    ) {
      inboxEntries {
        ...InboxEntryFields
      }
      exclusiveStartKey
    }
  }
  ${INBOX_ENTRY_FIELDS}
`;

const GET_PAGINATED_INBOX_ENTRIES_BY_SUBGROUP = gql`
  query getPaginatedInboxEntriesBySubgroup(
    $taskGroupId: String!
    $taskSubgroupId: String!
    $limit: Int!
    $exclusiveStartKey: String
    $filters: InboxEntryFiltersInput
    $sort: InboxEntrySortInput
  ) {
    getPaginatedInboxEntriesBySubgroup(
      taskGroupId: $taskGroupId
      taskSubgroupId: $taskSubgroupId
      limit: $limit
      exclusiveStartKey: $exclusiveStartKey
      filters: $filters
      sort: $sort
    ) {
      inboxEntries {
        ...InboxEntryFields
      }
      exclusiveStartKey
    }
  }
  ${INBOX_ENTRY_FIELDS}
`;

const GET_INITIAL_DOBOX_ENTRIES = gql`
  query getInitialDoBoxEntries(
    $taskGroupId: String!
    $subgroupIds: [String!]!
    $limitPerBucket: Int!
    $filters: InboxEntryFiltersInput
    $sort: InboxEntrySortInput
    $bucketLimits: [DoBoxBucketLimitInput!]
  ) {
    getInitialDoBoxEntries(
      taskGroupId: $taskGroupId
      subgroupIds: $subgroupIds
      limitPerBucket: $limitPerBucket
      filters: $filters
      sort: $sort
      bucketLimits: $bucketLimits
    ) {
      bucketKey
      inboxEntries {
        ...InboxEntryFields
      }
      exclusiveStartKey
      totalCount
    }
  }
  ${INBOX_ENTRY_FIELDS}
`;

interface GetPaginatedInboxEntriesParams {
  taskGroupId: string;
  limit: number;
  exclusiveStartKey?: string;
  filters?: InboxEntryFilters;
  sort?: InboxEntrySort;
  signal?: AbortSignal;
}

interface GetPaginatedInboxEntriesData {
  getPaginatedInboxEntries: {
    inboxEntries: InboxEntry[];
    exclusiveStartKey: string | null;
  };
}

interface GetPaginatedInboxEntriesBySubgroupParams {
  taskGroupId: string;
  taskSubgroupId: string;
  limit: number;
  exclusiveStartKey?: string;
  filters?: InboxEntryFilters;
  sort?: InboxEntrySort;
  signal?: AbortSignal;
}

interface GetPaginatedInboxEntriesBySubgroupData {
  getPaginatedInboxEntriesBySubgroup: {
    inboxEntries: InboxEntry[];
    exclusiveStartKey: string | null;
  };
}

export interface DoBoxBucketEntriesResult {
  bucketKey: string;
  inboxEntries: InboxEntry[];
  exclusiveStartKey: string | null;
  // Undefined only when filters.unreadOnly is set — the backend skips the count sweep in that case.
  totalCount?: number;
}

export interface DoBoxBucketLimitInput {
  bucketKey: string;
  limit: number;
}

interface GetInitialDoBoxEntriesParams {
  taskGroupId: string;
  subgroupIds: string[];
  limitPerBucket: number;
  filters?: InboxEntryFilters;
  sort?: InboxEntrySort;
  bucketLimits?: DoBoxBucketLimitInput[];
  signal?: AbortSignal;
}

interface GetInitialDoBoxEntriesData {
  getInitialDoBoxEntries: DoBoxBucketEntriesResult[];
}

interface GetDetailedInboxEntryData {
  getDetailedInboxEntry: InboxEntry | null;
}

interface CreateInboxEntryData {
  createInboxEntry: InboxEntry;
}

interface UpdateInboxEntryData {
  updateInboxEntry: InboxEntry;
}

interface DeleteInboxEntryData {
  deleteInboxEntry: boolean;
}

export function useGetPaginatedInboxEntries() {
  const [runQuery, { loading }] = useLazyQuery<GetPaginatedInboxEntriesData>(
    GET_PAGINATED_INBOX_ENTRIES,
    {
      fetchPolicy: 'network-only',
    },
  );

  const getPaginatedInboxEntries = useCallback(
    async ({
      taskGroupId,
      limit,
      exclusiveStartKey,
      filters,
      sort,
      signal,
    }: GetPaginatedInboxEntriesParams): Promise<{
      inboxEntries: InboxEntry[];
      exclusiveStartKey: string | null;
    }> => {
      const { data } = await runQuery({
        variables: { taskGroupId, limit, exclusiveStartKey, filters, sort },
        context: buildAbortableQueryContext(signal),
      });

      return (
        data?.getPaginatedInboxEntries ?? {
          inboxEntries: [],
          exclusiveStartKey: null,
        }
      );
    },
    [runQuery],
  );

  return { getPaginatedInboxEntries, loading };
}

export function useGetPaginatedInboxEntriesBySubgroup() {
  const client = useApolloClient();

  const getPaginatedInboxEntriesBySubgroup = useCallback(
    async ({
      taskGroupId,
      taskSubgroupId,
      limit,
      exclusiveStartKey,
      filters,
      sort,
      signal,
    }: GetPaginatedInboxEntriesBySubgroupParams): Promise<{
      inboxEntries: InboxEntry[];
      exclusiveStartKey: string | null;
    }> => {
      const { data } =
        await client.query<GetPaginatedInboxEntriesBySubgroupData>({
          query: GET_PAGINATED_INBOX_ENTRIES_BY_SUBGROUP,
          variables: {
            taskGroupId,
            taskSubgroupId,
            limit,
            exclusiveStartKey,
            filters,
            sort,
          },
          fetchPolicy: 'network-only',
          context: buildAbortableQueryContext(signal),
        });

      return (
        data?.getPaginatedInboxEntriesBySubgroup ?? {
          inboxEntries: [],
          exclusiveStartKey: null,
        }
      );
    },
    [client],
  );

  return { getPaginatedInboxEntriesBySubgroup };
}

export function useGetInitialDoBoxEntries() {
  const [runQuery, { loading }] = useLazyQuery<GetInitialDoBoxEntriesData>(
    GET_INITIAL_DOBOX_ENTRIES,
    {
      fetchPolicy: 'network-only',
    },
  );

  const getInitialDoBoxEntries = useCallback(
    async ({
      taskGroupId,
      subgroupIds,
      limitPerBucket,
      filters,
      sort,
      bucketLimits,
      signal,
    }: GetInitialDoBoxEntriesParams): Promise<DoBoxBucketEntriesResult[]> => {
      const { data } = await runQuery({
        variables: {
          taskGroupId,
          subgroupIds,
          limitPerBucket,
          filters,
          sort,
          bucketLimits,
        },
        context: buildAbortableQueryContext(signal),
      });

      return data?.getInitialDoBoxEntries ?? [];
    },
    [runQuery],
  );

  return { getInitialDoBoxEntries, loading };
}

export const GET_DETAILED_INBOX_ENTRY = gql`
  query getDetailedInboxEntry(
    $inboxEntryId: String!
    $includeChatActivity: Boolean = false
  ) {
    getDetailedInboxEntry(
      inboxEntryId: $inboxEntryId
      includeChatActivity: $includeChatActivity
    ) {
      ...InboxEntryFields
      agentRunHistory {
        ...AgentRunSummaryFields
      }
      # Detail query only (one read per entry); the API ships before the web build that selects it.
      originFlow {
        flowId
        name
      }
      chatActivity @include(if: $includeChatActivity) {
        unitKey
        platform
        scope
        linkedMessageIds
        newReplyMessageIds
        truncated
        messagesHidden
        messages {
          ...InboxEntryCommunicationFields
        }
      }
      relatedInboxEntries {
        id
        subject
        resolvedAt
        dueAt
        taskLabelIds
        participantIds
        taskGroupId
        taskSubgroupId
      }
    }
  }
  ${INBOX_ENTRY_FIELDS}
  ${AGENT_RUN_SUMMARY_FIELDS}
`;

interface UseGetDetailedInboxEntryOptions {
  includeChatActivity?: boolean;
}

export function useGetDetailedInboxEntry({
  includeChatActivity = false,
}: UseGetDetailedInboxEntryOptions = {}) {
  const { loading, refetch } = useQuery<GetDetailedInboxEntryData>(
    GET_DETAILED_INBOX_ENTRY,
    {
      skip: true,
    },
  );

  const getDetailedInboxEntry = useCallback(
    async (inboxEntryId: string) => {
      const { data } = await refetch({
        inboxEntryId,
        includeChatActivity,
      });
      return data?.getDetailedInboxEntry ?? null;
    },
    [refetch, includeChatActivity],
  );

  return { getDetailedInboxEntry, loading };
}

interface UseDetailedInboxEntryOptions {
  skip?: boolean;
}

export function useDetailedInboxEntry(
  inboxEntryId: string,
  { skip }: UseDetailedInboxEntryOptions = {},
) {
  const { data, loading } = useQuery<GetDetailedInboxEntryData>(
    GET_DETAILED_INBOX_ENTRY,
    {
      variables: { inboxEntryId },
      skip,
      fetchPolicy: FETCH_POLICY_CACHE_AND_NETWORK,
    },
  );

  return {
    entry: data?.getDetailedInboxEntry ?? undefined,
    loading,
  };
}

const CREATE_INBOX_ENTRY = gql`
  mutation createInboxEntry($inboxEntry: CreateInboxEntryInput!) {
    createInboxEntry(inboxEntry: $inboxEntry) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

export function useCreateInboxEntry() {
  const [createInboxEntryMutation, { loading, error }] =
    useMutation<CreateInboxEntryData>(CREATE_INBOX_ENTRY);

  const createInboxEntry = useCallback(
    async (inboxEntry: CreateInboxEntryInput) => {
      const { data } = await createInboxEntryMutation({
        variables: {
          inboxEntry,
        },
      });
      return data?.createInboxEntry;
    },
    [createInboxEntryMutation],
  );

  return { createInboxEntry, loading, error };
}

const UPDATE_INBOX_ENTRY = gql`
  mutation updateInboxEntry($inboxEntry: UpdateInboxEntryInput!) {
    updateInboxEntry(inboxEntry: $inboxEntry) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

export function useUpdateInboxEntry() {
  const [updateInboxEntryMutation, { loading, error }] =
    useMutation<UpdateInboxEntryData>(UPDATE_INBOX_ENTRY);

  const updateInboxEntry = useCallback(
    async (inboxEntry: UpdateInboxEntryInput) => {
      const { data } = await updateInboxEntryMutation({
        variables: {
          inboxEntry,
        },
      });
      return data?.updateInboxEntry;
    },
    [updateInboxEntryMutation],
  );

  return { updateInboxEntry, loading, error };
}

const DISMISS_RESOLUTION_SUGGESTION = gql`
  mutation dismissResolutionSuggestion(
    $inboxEntryId: String!
    $seenResolutionSuggestedAt: Timestamp
  ) {
    dismissResolutionSuggestion(
      inboxEntryId: $inboxEntryId
      seenResolutionSuggestedAt: $seenResolutionSuggestedAt
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface DismissResolutionSuggestionData {
  dismissResolutionSuggestion?: InboxEntry;
}

export function useDismissResolutionSuggestionService() {
  const [dismissMutation, { loading, error }] =
    useMutation<DismissResolutionSuggestionData>(DISMISS_RESOLUTION_SUGGESTION);

  // The flag the user saw: the server leaves a newer one alone.
  const dismissResolutionSuggestion = useCallback(
    async (inboxEntryId: string, seenResolutionSuggestedAt?: number | null) => {
      const { data } = await dismissMutation({
        variables: { inboxEntryId, seenResolutionSuggestedAt },
      });
      return data?.dismissResolutionSuggestion;
    },
    [dismissMutation],
  );

  return { dismissResolutionSuggestion, loading, error };
}

const DISMISS_INBOX_ENTRY_REPLIES = gql`
  mutation dismissInboxEntryReplies(
    $inboxEntryId: String!
    $messageIds: [String!]!
  ) {
    dismissInboxEntryReplies(
      inboxEntryId: $inboxEntryId
      messageIds: $messageIds
    ) {
      id
      newReplies {
        unitKey
        messageIds
      }
    }
  }
`;

interface DismissInboxEntryRepliesData {
  dismissInboxEntryReplies?: Pick<InboxEntry, 'id' | 'newReplies'>;
}

export function useDismissInboxEntryRepliesService() {
  const [dismissMutation, { loading, error }] =
    useMutation<DismissInboxEntryRepliesData>(DISMISS_INBOX_ENTRY_REPLIES);

  // The replies the viewer saw: the server keeps only those still new.
  const dismissInboxEntryReplies = useCallback(
    async (inboxEntryId: string, messageIds: string[]) => {
      const { data } = await dismissMutation({
        variables: { inboxEntryId, messageIds },
      });
      return data?.dismissInboxEntryReplies;
    },
    [dismissMutation],
  );

  return { dismissInboxEntryReplies, loading, error };
}

const DELETE_INBOX_ENTRY = gql`
  mutation deleteInboxEntry($inboxEntryId: String!) {
    deleteInboxEntry(inboxEntryId: $inboxEntryId)
  }
`;

export function useDeleteInboxEntry() {
  const [deleteInboxEntryMutation, { loading, error }] =
    useMutation<DeleteInboxEntryData>(DELETE_INBOX_ENTRY, {
      refetchQueries: ['getAgentExecutionHistory'],
    });

  const deleteInboxEntry = useCallback(
    async (inboxEntryId: string) => {
      const { data } = await deleteInboxEntryMutation({
        variables: {
          inboxEntryId,
        },
      });
      return data?.deleteInboxEntry ?? false;
    },
    [deleteInboxEntryMutation],
  );

  return { deleteInboxEntry, loading, error };
}

const MOVE_INBOX_ENTRY_TO_TASK_GROUP = gql`
  mutation moveInboxEntryToTaskGroup($inboxEntryId: String!, $newTaskGroupId: String!) {
    moveInboxEntryToTaskGroup(inboxEntryId: $inboxEntryId, newTaskGroupId: $newTaskGroupId) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface MoveInboxEntryToTaskGroupData {
  moveInboxEntryToTaskGroup: InboxEntry;
}

export function useMoveInboxEntryToTaskGroupService() {
  const [moveInboxEntryToTaskGroupMutation, { loading, error }] =
    useMutation<MoveInboxEntryToTaskGroupData>(MOVE_INBOX_ENTRY_TO_TASK_GROUP);

  const moveInboxEntryToTaskGroup = useCallback(
    async (inboxEntryId: string, newTaskGroupId: string) => {
      const { data } = await moveInboxEntryToTaskGroupMutation({
        variables: {
          inboxEntryId,
          newTaskGroupId,
        },
      });
      return data?.moveInboxEntryToTaskGroup;
    },
    [moveInboxEntryToTaskGroupMutation],
  );

  return { moveInboxEntryToTaskGroup, loading, error };
}

const MOVE_INBOX_ENTRY_TO_TASK_SUBGROUP = gql`
  mutation moveInboxEntryToTaskSubgroup(
    $inboxEntryId: String!
    $taskSubgroupId: String
    $orderKey: String
  ) {
    moveInboxEntryToTaskSubgroup(
      inboxEntryId: $inboxEntryId
      taskSubgroupId: $taskSubgroupId
      orderKey: $orderKey
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface MoveInboxEntryToTaskSubgroupData {
  moveInboxEntryToTaskSubgroup: InboxEntry;
}

export function useMoveInboxEntryToTaskSubgroupService() {
  const [moveInboxEntryToTaskSubgroupMutation, { loading, error }] =
    useMutation<MoveInboxEntryToTaskSubgroupData>(
      MOVE_INBOX_ENTRY_TO_TASK_SUBGROUP,
    );

  const moveInboxEntryToTaskSubgroup = useCallback(
    async (
      inboxEntryId: string,
      taskSubgroupId: string | null,
      orderKey?: string,
    ) => {
      const { data } = await moveInboxEntryToTaskSubgroupMutation({
        variables: { inboxEntryId, taskSubgroupId, orderKey },
      });
      return data?.moveInboxEntryToTaskSubgroup;
    },
    [moveInboxEntryToTaskSubgroupMutation],
  );

  return { moveInboxEntryToTaskSubgroup, loading, error };
}

const SET_INBOX_ENTRY_ORDER_KEYS = gql`
  mutation setInboxEntryOrderKeys(
    $taskGroupId: String!
    $assignments: [InboxEntryOrderKeyInput!]!
  ) {
    setInboxEntryOrderKeys(taskGroupId: $taskGroupId, assignments: $assignments) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface SetInboxEntryOrderKeysData {
  setInboxEntryOrderKeys: InboxEntry[];
}

export function useSetInboxEntryOrderKeysService() {
  const [setInboxEntryOrderKeysMutation, { loading, error }] =
    useMutation<SetInboxEntryOrderKeysData>(SET_INBOX_ENTRY_ORDER_KEYS);

  const setInboxEntryOrderKeys = useCallback(
    async (
      taskGroupId: string,
      assignments: InboxEntryOrderKeyAssignment[],
    ) => {
      const { data } = await setInboxEntryOrderKeysMutation({
        variables: { taskGroupId, assignments },
      });
      return data?.setInboxEntryOrderKeys ?? [];
    },
    [setInboxEntryOrderKeysMutation],
  );

  return { setInboxEntryOrderKeys, loading, error };
}

const UPDATE_INBOX_ENTRY_COLLECTIONS = gql`
  mutation updateInboxEntryCollections(
    $inboxEntryId: String!
    $collectionMutations: InboxEntryCollectionMutations!
  ) {
    updateInboxEntryCollections(
      inboxEntryId: $inboxEntryId
      collectionMutations: $collectionMutations
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface UpdateInboxEntryCollectionsData {
  updateInboxEntryCollections: InboxEntry | null;
}

export function useUpdateInboxEntryCollections() {
  const [updateInboxEntryCollectionsMutation, { loading, error }] =
    useMutation<UpdateInboxEntryCollectionsData>(
      UPDATE_INBOX_ENTRY_COLLECTIONS,
    );

  const updateInboxEntryCollections = useCallback(
    async (
      inboxEntryId: string,
      collectionMutations: InboxEntryCollectionMutations,
    ) => {
      const { data } = await updateInboxEntryCollectionsMutation({
        variables: {
          inboxEntryId,
          collectionMutations,
        },
      });
      return data?.updateInboxEntryCollections ?? null;
    },
    [updateInboxEntryCollectionsMutation],
  );

  return { updateInboxEntryCollections, loading, error };
}

const GET_UNREAD_INBOX_ENTRIES = gql`
  query GetUnreadInboxEntries {
    getUnreadInboxEntries {
      taskGroupId
      inboxEntryIds
    }
  }
`;

interface UnreadInboxEntriesData {
  getUnreadInboxEntries: UnreadInboxEntriesByTaskGroup[];
}

export function useGetUnreadInboxEntries() {
  const { refetch, loading } = useQuery<UnreadInboxEntriesData>(
    GET_UNREAD_INBOX_ENTRIES,
    { skip: true },
  );

  const getUnreadInboxEntries = useCallback(async () => {
    const { data } = await refetch();
    return data?.getUnreadInboxEntries ?? [];
  }, [refetch]);

  return { getUnreadInboxEntries, loading };
}

const MARK_INBOX_ENTRY_AS_VIEWED = gql`
  mutation MarkInboxEntryAsViewed($inboxEntryId: String!) {
    markInboxEntryAsViewed(inboxEntryId: $inboxEntryId)
  }
`;

interface MarkInboxEntryAsViewedData {
  markInboxEntryAsViewed: boolean;
}

export function useMarkInboxEntryAsViewedService() {
  const [markInboxEntryAsViewedMutation, { loading, error }] =
    useMutation<MarkInboxEntryAsViewedData>(MARK_INBOX_ENTRY_AS_VIEWED);

  const markInboxEntryAsViewed = useCallback(
    async (inboxEntryId: string): Promise<boolean> => {
      const { data } = await markInboxEntryAsViewedMutation({
        variables: { inboxEntryId },
      });
      return data?.markInboxEntryAsViewed ?? false;
    },
    [markInboxEntryAsViewedMutation],
  );

  return { markInboxEntryAsViewed, loading, error };
}

const ADD_CHECKLIST = gql`
  mutation addChecklist(
    $inboxEntryId: String!
    $title: String!
    $id: String
  ) {
    addChecklist(inboxEntryId: $inboxEntryId, title: $title, id: $id) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface AddChecklistData {
  addChecklist: InboxEntry | null;
}

export function useAddChecklist() {
  const [addChecklistMutation, { loading, error }] =
    useMutation<AddChecklistData>(ADD_CHECKLIST);

  const addChecklist = useCallback(
    async (inboxEntryId: string, title: string, id?: string) => {
      const { data } = await addChecklistMutation({
        variables: { inboxEntryId, title, id },
      });
      return data?.addChecklist ?? null;
    },
    [addChecklistMutation],
  );

  return { addChecklist, loading, error };
}

const REMOVE_CHECKLIST = gql`
  mutation removeChecklist($inboxEntryId: String!, $checklistId: String!) {
    removeChecklist(inboxEntryId: $inboxEntryId, checklistId: $checklistId) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface RemoveChecklistData {
  removeChecklist: InboxEntry | null;
}

export function useRemoveChecklist() {
  const [removeChecklistMutation, { loading, error }] =
    useMutation<RemoveChecklistData>(REMOVE_CHECKLIST);

  const removeChecklist = useCallback(
    async (inboxEntryId: string, checklistId: string) => {
      const { data } = await removeChecklistMutation({
        variables: { inboxEntryId, checklistId },
      });
      return data?.removeChecklist ?? null;
    },
    [removeChecklistMutation],
  );

  return { removeChecklist, loading, error };
}

const RENAME_CHECKLIST = gql`
  mutation renameChecklist(
    $inboxEntryId: String!
    $checklistId: String!
    $title: String!
  ) {
    renameChecklist(
      inboxEntryId: $inboxEntryId
      checklistId: $checklistId
      title: $title
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface RenameChecklistData {
  renameChecklist: InboxEntry | null;
}

export function useRenameChecklist() {
  const [renameChecklistMutation, { loading, error }] =
    useMutation<RenameChecklistData>(RENAME_CHECKLIST);

  const renameChecklist = useCallback(
    async (inboxEntryId: string, checklistId: string, title: string) => {
      const { data } = await renameChecklistMutation({
        variables: { inboxEntryId, checklistId, title },
      });
      return data?.renameChecklist ?? null;
    },
    [renameChecklistMutation],
  );

  return { renameChecklist, loading, error };
}

const ADD_CHECKLIST_SUBTASK = gql`
  mutation addChecklistSubtask(
    $inboxEntryId: String!
    $checklistId: String!
    $title: String!
    $id: String
  ) {
    addChecklistSubtask(
      inboxEntryId: $inboxEntryId
      checklistId: $checklistId
      title: $title
      id: $id
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface AddChecklistSubtaskData {
  addChecklistSubtask: InboxEntry | null;
}

export function useAddChecklistSubtask() {
  const [addChecklistSubtaskMutation, { loading, error }] =
    useMutation<AddChecklistSubtaskData>(ADD_CHECKLIST_SUBTASK);

  const addChecklistSubtask = useCallback(
    async (
      inboxEntryId: string,
      checklistId: string,
      title: string,
      id?: string,
    ) => {
      const { data } = await addChecklistSubtaskMutation({
        variables: { inboxEntryId, checklistId, title, id },
      });
      return data?.addChecklistSubtask ?? null;
    },
    [addChecklistSubtaskMutation],
  );

  return { addChecklistSubtask, loading, error };
}

const REMOVE_CHECKLIST_SUBTASK = gql`
  mutation removeChecklistSubtask(
    $inboxEntryId: String!
    $checklistId: String!
    $subtaskId: String!
  ) {
    removeChecklistSubtask(
      inboxEntryId: $inboxEntryId
      checklistId: $checklistId
      subtaskId: $subtaskId
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface RemoveChecklistSubtaskData {
  removeChecklistSubtask: InboxEntry | null;
}

export function useRemoveChecklistSubtask() {
  const [removeChecklistSubtaskMutation, { loading, error }] =
    useMutation<RemoveChecklistSubtaskData>(REMOVE_CHECKLIST_SUBTASK);

  const removeChecklistSubtask = useCallback(
    async (inboxEntryId: string, checklistId: string, subtaskId: string) => {
      const { data } = await removeChecklistSubtaskMutation({
        variables: { inboxEntryId, checklistId, subtaskId },
      });
      return data?.removeChecklistSubtask ?? null;
    },
    [removeChecklistSubtaskMutation],
  );

  return { removeChecklistSubtask, loading, error };
}

const RENAME_CHECKLIST_SUBTASK = gql`
  mutation renameChecklistSubtask(
    $inboxEntryId: String!
    $checklistId: String!
    $subtaskId: String!
    $title: String!
  ) {
    renameChecklistSubtask(
      inboxEntryId: $inboxEntryId
      checklistId: $checklistId
      subtaskId: $subtaskId
      title: $title
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface RenameChecklistSubtaskData {
  renameChecklistSubtask: InboxEntry | null;
}

export function useRenameChecklistSubtask() {
  const [renameChecklistSubtaskMutation, { loading, error }] =
    useMutation<RenameChecklistSubtaskData>(RENAME_CHECKLIST_SUBTASK);

  const renameChecklistSubtask = useCallback(
    async (
      inboxEntryId: string,
      checklistId: string,
      subtaskId: string,
      title: string,
    ) => {
      const { data } = await renameChecklistSubtaskMutation({
        variables: { inboxEntryId, checklistId, subtaskId, title },
      });
      return data?.renameChecklistSubtask ?? null;
    },
    [renameChecklistSubtaskMutation],
  );

  return { renameChecklistSubtask, loading, error };
}

const TOGGLE_CHECKLIST_SUBTASK = gql`
  mutation toggleChecklistSubtask(
    $inboxEntryId: String!
    $checklistId: String!
    $subtaskId: String!
  ) {
    toggleChecklistSubtask(
      inboxEntryId: $inboxEntryId
      checklistId: $checklistId
      subtaskId: $subtaskId
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface ToggleChecklistSubtaskData {
  toggleChecklistSubtask: InboxEntry | null;
}

export function useToggleChecklistSubtask() {
  const [toggleChecklistSubtaskMutation, { loading, error }] =
    useMutation<ToggleChecklistSubtaskData>(TOGGLE_CHECKLIST_SUBTASK);

  const toggleChecklistSubtask = useCallback(
    async (inboxEntryId: string, checklistId: string, subtaskId: string) => {
      const { data } = await toggleChecklistSubtaskMutation({
        variables: { inboxEntryId, checklistId, subtaskId },
      });
      return data?.toggleChecklistSubtask ?? null;
    },
    [toggleChecklistSubtaskMutation],
  );

  return { toggleChecklistSubtask, loading, error };
}

export interface DelegateInboxEntryToAgentInput {
  taskType: AgentDelegationTaskType;
  localRuntime?: LocalCodingAgentRuntime;
  localModel?: string;
  localAgentProfile?: string;
  mcpConnectionId?: string;
  agentTemplateId?: string;
  instructions?: string;
}

const DELEGATE_INBOX_ENTRY_TO_AGENT = gql`
  mutation delegateInboxEntryToAgent(
    $taskGroupId: String!
    $entryId: String!
    $delegationId: ID
    $input: DelegateInboxEntryInput!
  ) {
    delegateInboxEntryToAgent(
      taskGroupId: $taskGroupId
      entryId: $entryId
      delegationId: $delegationId
      input: $input
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface DelegateInboxEntryToAgentData {
  delegateInboxEntryToAgent: InboxEntry;
}

export function useDelegateInboxEntryToAgent() {
  const [delegateInboxEntryToAgentMutation, { loading, error }] =
    useMutation<DelegateInboxEntryToAgentData>(DELEGATE_INBOX_ENTRY_TO_AGENT);

  const delegateInboxEntryToAgent = useCallback(
    async (
      taskGroupId: string,
      entryId: string,
      input: DelegateInboxEntryToAgentInput,
      delegationId?: string,
    ) => {
      const { data } = await delegateInboxEntryToAgentMutation({
        variables: { taskGroupId, entryId, input, delegationId },
      });
      return data?.delegateInboxEntryToAgent ?? null;
    },
    [delegateInboxEntryToAgentMutation],
  );

  return { delegateInboxEntryToAgent, loading, error };
}

const ANSWER_INBOX_ENTRY_DELEGATION = gql`
  mutation answerInboxEntryDelegation(
    $taskGroupId: String!
    $entryId: String!
    $delegationId: ID
    $answer: String!
    $localModel: String
  ) {
    answerInboxEntryDelegation(
      taskGroupId: $taskGroupId
      entryId: $entryId
      delegationId: $delegationId
      answer: $answer
      localModel: $localModel
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface AnswerInboxEntryDelegationData {
  answerInboxEntryDelegation: InboxEntry;
}

export function useAnswerInboxEntryDelegation() {
  const [answerInboxEntryDelegationMutation, { loading, error }] =
    useMutation<AnswerInboxEntryDelegationData>(ANSWER_INBOX_ENTRY_DELEGATION);

  const answerInboxEntryDelegation = useCallback(
    async (
      taskGroupId: string,
      entryId: string,
      answer: string,
      localModel?: string,
      delegationId?: string,
    ) => {
      const { data } = await answerInboxEntryDelegationMutation({
        variables: {
          taskGroupId,
          entryId,
          delegationId,
          answer,
          ...(localModel !== undefined && { localModel }),
        },
      });
      return data?.answerInboxEntryDelegation ?? null;
    },
    [answerInboxEntryDelegationMutation],
  );

  return { answerInboxEntryDelegation, loading, error };
}

// Structured replacement for approving a plan through answerInboxEntryDelegation with the
// translated "Aprovar"/"Approve" button label: the client sends back the planHash it
// rendered, so the server can refuse a plan that changed underneath an open modal
// (CONFLICT/DELEGATION_PLAN_CHANGED) instead of approving against stale content.
const APPROVE_INBOX_ENTRY_DELEGATION_PLAN = gql`
  mutation approveInboxEntryDelegationPlan(
    $taskGroupId: String!
    $entryId: String!
    $delegationId: ID
    $planHash: String!
    $localModel: String
  ) {
    approveInboxEntryDelegationPlan(
      taskGroupId: $taskGroupId
      entryId: $entryId
      delegationId: $delegationId
      planHash: $planHash
      localModel: $localModel
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface ApproveInboxEntryDelegationPlanData {
  approveInboxEntryDelegationPlan: InboxEntry;
}

export function useApproveInboxEntryDelegationPlan() {
  const [approveInboxEntryDelegationPlanMutation, { loading, error }] =
    useMutation<ApproveInboxEntryDelegationPlanData>(
      APPROVE_INBOX_ENTRY_DELEGATION_PLAN,
    );

  const approveInboxEntryDelegationPlan = useCallback(
    async (
      taskGroupId: string,
      entryId: string,
      planHash: string,
      localModel?: string,
      delegationId?: string,
    ) => {
      const { data } = await approveInboxEntryDelegationPlanMutation({
        variables: {
          taskGroupId,
          entryId,
          delegationId,
          planHash,
          ...(localModel !== undefined && { localModel }),
        },
      });
      return data?.approveInboxEntryDelegationPlan ?? null;
    },
    [approveInboxEntryDelegationPlanMutation],
  );

  return { approveInboxEntryDelegationPlan, loading, error };
}

const REFINE_INBOX_ENTRY_DELEGATION_PLAN = gql`
  mutation refineInboxEntryDelegationPlan(
    $taskGroupId: String!
    $entryId: String!
    $delegationId: ID
    $feedback: String!
  ) {
    refineInboxEntryDelegationPlan(
      taskGroupId: $taskGroupId
      entryId: $entryId
      delegationId: $delegationId
      feedback: $feedback
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface RefineInboxEntryDelegationPlanData {
  refineInboxEntryDelegationPlan: InboxEntry;
}

export function useRefineInboxEntryDelegationPlan() {
  const [refineInboxEntryDelegationPlanMutation, { loading, error }] =
    useMutation<RefineInboxEntryDelegationPlanData>(
      REFINE_INBOX_ENTRY_DELEGATION_PLAN,
    );

  const refineInboxEntryDelegationPlan = useCallback(
    async (
      taskGroupId: string,
      entryId: string,
      feedback: string,
      delegationId?: string,
    ) => {
      const { data } = await refineInboxEntryDelegationPlanMutation({
        variables: { taskGroupId, entryId, delegationId, feedback },
      });
      return data?.refineInboxEntryDelegationPlan ?? null;
    },
    [refineInboxEntryDelegationPlanMutation],
  );

  return { refineInboxEntryDelegationPlan, loading, error };
}

const REQUEST_INBOX_ENTRY_DELEGATION_PR_CHANGES = gql`
  mutation requestInboxEntryDelegationPrChanges(
    $taskGroupId: String!
    $entryId: String!
    $delegationId: ID
    $feedback: String!
    $attachmentIds: [String!]
  ) {
    requestInboxEntryDelegationPrChanges(
      taskGroupId: $taskGroupId
      entryId: $entryId
      delegationId: $delegationId
      feedback: $feedback
      attachmentIds: $attachmentIds
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface RequestInboxEntryDelegationPrChangesData {
  requestInboxEntryDelegationPrChanges: InboxEntry;
}

export function useRequestInboxEntryDelegationPrChanges() {
  const [requestInboxEntryDelegationPrChangesMutation, { loading, error }] =
    useMutation<RequestInboxEntryDelegationPrChangesData>(
      REQUEST_INBOX_ENTRY_DELEGATION_PR_CHANGES,
    );

  const requestInboxEntryDelegationPrChanges = useCallback(
    async (
      taskGroupId: string,
      entryId: string,
      feedback: string,
      attachmentIds?: string[],
      delegationId?: string,
    ) => {
      const { data } = await requestInboxEntryDelegationPrChangesMutation({
        variables: {
          taskGroupId,
          entryId,
          delegationId,
          feedback,
          attachmentIds,
        },
      });
      return data?.requestInboxEntryDelegationPrChanges ?? null;
    },
    [requestInboxEntryDelegationPrChangesMutation],
  );

  return { requestInboxEntryDelegationPrChanges, loading, error };
}

const RESOLVE_INBOX_ENTRY_DELEGATION_PR_COMMENTS = gql`
  mutation resolveInboxEntryDelegationPrComments(
    $taskGroupId: String!
    $entryId: String!
    $delegationId: ID
  ) {
    resolveInboxEntryDelegationPrComments(
      taskGroupId: $taskGroupId
      entryId: $entryId
      delegationId: $delegationId
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface ResolveInboxEntryDelegationPrCommentsData {
  resolveInboxEntryDelegationPrComments: InboxEntry;
}

export function useResolveInboxEntryDelegationPrComments() {
  const [resolveInboxEntryDelegationPrCommentsMutation, { loading, error }] =
    useMutation<ResolveInboxEntryDelegationPrCommentsData>(
      RESOLVE_INBOX_ENTRY_DELEGATION_PR_COMMENTS,
    );

  const resolveInboxEntryDelegationPrComments = useCallback(
    async (taskGroupId: string, entryId: string, delegationId?: string) => {
      const { data } = await resolveInboxEntryDelegationPrCommentsMutation({
        variables: { taskGroupId, entryId, delegationId },
      });
      return data?.resolveInboxEntryDelegationPrComments ?? null;
    },
    [resolveInboxEntryDelegationPrCommentsMutation],
  );

  return { resolveInboxEntryDelegationPrComments, loading, error };
}

const CONFIRM_INBOX_ENTRY_DELEGATION_ACTIONS = gql`
  mutation confirmInboxEntryDelegationActions(
    $taskGroupId: String!
    $entryId: String!
    $delegationId: ID
    $confirmedActionIds: [String!]!
  ) {
    confirmInboxEntryDelegationActions(
      taskGroupId: $taskGroupId
      entryId: $entryId
      delegationId: $delegationId
      confirmedActionIds: $confirmedActionIds
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface ConfirmInboxEntryDelegationActionsData {
  confirmInboxEntryDelegationActions: InboxEntry;
}

export function useConfirmInboxEntryDelegationActions() {
  const [confirmInboxEntryDelegationActionsMutation, { loading, error }] =
    useMutation<ConfirmInboxEntryDelegationActionsData>(
      CONFIRM_INBOX_ENTRY_DELEGATION_ACTIONS,
    );

  const confirmInboxEntryDelegationActions = useCallback(
    async (
      taskGroupId: string,
      entryId: string,
      confirmedActionIds: string[],
      delegationId?: string,
    ) => {
      const { data } = await confirmInboxEntryDelegationActionsMutation({
        variables: { taskGroupId, entryId, delegationId, confirmedActionIds },
      });
      return data?.confirmInboxEntryDelegationActions ?? null;
    },
    [confirmInboxEntryDelegationActionsMutation],
  );

  return { confirmInboxEntryDelegationActions, loading, error };
}

const CANCEL_INBOX_ENTRY_DELEGATION = gql`
  mutation cancelInboxEntryDelegation(
    $taskGroupId: String!
    $entryId: String!
    $delegationId: ID
  ) {
    cancelInboxEntryDelegation(
      taskGroupId: $taskGroupId
      entryId: $entryId
      delegationId: $delegationId
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface CancelInboxEntryDelegationData {
  cancelInboxEntryDelegation: InboxEntry;
}

export function useCancelInboxEntryDelegation() {
  const [cancelInboxEntryDelegationMutation, { loading, error }] =
    useMutation<CancelInboxEntryDelegationData>(CANCEL_INBOX_ENTRY_DELEGATION);

  const cancelInboxEntryDelegation = useCallback(
    async (taskGroupId: string, entryId: string, delegationId?: string) => {
      const { data } = await cancelInboxEntryDelegationMutation({
        variables: { taskGroupId, entryId, delegationId },
      });
      return data?.cancelInboxEntryDelegation ?? null;
    },
    [cancelInboxEntryDelegationMutation],
  );

  return { cancelInboxEntryDelegation, loading, error };
}

const SET_DELEGATION_SKIP_APPROVALS = gql`
  mutation setDelegationSkipApprovals($taskGroupId: ID!, $entryId: ID!, $skip: Boolean!) {
    setDelegationSkipApprovals(taskGroupId: $taskGroupId, entryId: $entryId, skip: $skip) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface SetDelegationSkipApprovalsData {
  setDelegationSkipApprovals: InboxEntry;
}

export function useSetDelegationSkipApprovals() {
  const [setDelegationSkipApprovalsMutation, { loading, error }] =
    useMutation<SetDelegationSkipApprovalsData>(SET_DELEGATION_SKIP_APPROVALS);

  const setDelegationSkipApprovals = useCallback(
    async (taskGroupId: string, entryId: string, skip: boolean) => {
      const { data } = await setDelegationSkipApprovalsMutation({
        variables: { taskGroupId, entryId, skip },
      });
      return data?.setDelegationSkipApprovals ?? null;
    },
    [setDelegationSkipApprovalsMutation],
  );

  return { setDelegationSkipApprovals, loading, error };
}

export interface ExecutorBulkApprovalResult {
  approvedCount: number;
  notExecutedCount: number;
  leftForIndividualCount: number;
  steps: ExecutorApprovalStep[];
}

const APPROVE_ALL_EXECUTOR_PROPOSALS = gql`
  mutation approveAllExecutorProposals($taskGroupId: ID!, $entryId: ID!, $skipFutureApprovals: Boolean) {
    approveAllExecutorProposals(taskGroupId: $taskGroupId, entryId: $entryId, skipFutureApprovals: $skipFutureApprovals) {
      approvedCount
      notExecutedCount
      leftForIndividualCount
      steps {
        label
        outcome
        reason
      }
    }
  }
`;

interface ApproveAllExecutorProposalsData {
  approveAllExecutorProposals: ExecutorBulkApprovalResult;
}

export function useApproveAllExecutorProposals() {
  const [approveAllMutation, { loading, error }] =
    useMutation<ApproveAllExecutorProposalsData>(
      APPROVE_ALL_EXECUTOR_PROPOSALS,
    );

  const approveAllExecutorProposals = useCallback(
    async (
      taskGroupId: string,
      entryId: string,
      skipFutureApprovals?: boolean,
    ) => {
      const { data } = await approveAllMutation({
        variables: { taskGroupId, entryId, skipFutureApprovals },
      });
      return data?.approveAllExecutorProposals ?? null;
    },
    [approveAllMutation],
  );

  return { approveAllExecutorProposals, loading, error };
}

const PAUSE_INBOX_ENTRY_DELEGATION = gql`
  mutation pauseInboxEntryDelegation(
    $taskGroupId: String!
    $entryId: String!
    $delegationId: ID
  ) {
    pauseInboxEntryDelegation(
      taskGroupId: $taskGroupId
      entryId: $entryId
      delegationId: $delegationId
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface PauseInboxEntryDelegationData {
  pauseInboxEntryDelegation: InboxEntry;
}

export function usePauseInboxEntryDelegation() {
  const [pauseInboxEntryDelegationMutation, { loading, error }] =
    useMutation<PauseInboxEntryDelegationData>(PAUSE_INBOX_ENTRY_DELEGATION);

  const pauseInboxEntryDelegation = useCallback(
    async (taskGroupId: string, entryId: string, delegationId?: string) => {
      const { data } = await pauseInboxEntryDelegationMutation({
        variables: { taskGroupId, entryId, delegationId },
      });
      return data?.pauseInboxEntryDelegation ?? null;
    },
    [pauseInboxEntryDelegationMutation],
  );

  return { pauseInboxEntryDelegation, loading, error };
}

const RESUME_INBOX_ENTRY_DELEGATION = gql`
  mutation resumeInboxEntryDelegation(
    $taskGroupId: String!
    $entryId: String!
    $delegationId: ID
  ) {
    resumeInboxEntryDelegation(
      taskGroupId: $taskGroupId
      entryId: $entryId
      delegationId: $delegationId
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface ResumeInboxEntryDelegationData {
  resumeInboxEntryDelegation: InboxEntry;
}

export function useResumeInboxEntryDelegation() {
  const [resumeInboxEntryDelegationMutation, { loading, error }] =
    useMutation<ResumeInboxEntryDelegationData>(RESUME_INBOX_ENTRY_DELEGATION);

  const resumeInboxEntryDelegation = useCallback(
    async (taskGroupId: string, entryId: string, delegationId?: string) => {
      const { data } = await resumeInboxEntryDelegationMutation({
        variables: { taskGroupId, entryId, delegationId },
      });
      return data?.resumeInboxEntryDelegation ?? null;
    },
    [resumeInboxEntryDelegationMutation],
  );

  return { resumeInboxEntryDelegation, loading, error };
}

const RETRY_INBOX_ENTRY_DELEGATION = gql`
  mutation retryInboxEntryDelegation(
    $taskGroupId: String!
    $entryId: String!
    $delegationId: ID
    $instructions: String
  ) {
    retryInboxEntryDelegation(
      taskGroupId: $taskGroupId
      entryId: $entryId
      delegationId: $delegationId
      instructions: $instructions
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface RetryInboxEntryDelegationData {
  retryInboxEntryDelegation: InboxEntry;
}

export function useRetryInboxEntryDelegation() {
  const [retryInboxEntryDelegationMutation, { loading, error }] =
    useMutation<RetryInboxEntryDelegationData>(RETRY_INBOX_ENTRY_DELEGATION);

  const retryInboxEntryDelegation = useCallback(
    async (
      taskGroupId: string,
      entryId: string,
      instructions?: string,
      delegationId?: string,
    ) => {
      const { data } = await retryInboxEntryDelegationMutation({
        variables: { taskGroupId, entryId, delegationId, instructions },
      });
      return data?.retryInboxEntryDelegation ?? null;
    },
    [retryInboxEntryDelegationMutation],
  );

  return { retryInboxEntryDelegation, loading, error };
}

const SYNC_INBOX_ENTRY_DELEGATION = gql`
  mutation syncInboxEntryDelegation(
    $taskGroupId: String!
    $entryId: String!
    $delegationId: ID
  ) {
    syncInboxEntryDelegation(
      taskGroupId: $taskGroupId
      entryId: $entryId
      delegationId: $delegationId
    ) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface SyncInboxEntryDelegationData {
  syncInboxEntryDelegation: InboxEntry;
}

export function useSyncInboxEntryDelegation() {
  const [syncInboxEntryDelegationMutation, { loading, error }] =
    useMutation<SyncInboxEntryDelegationData>(SYNC_INBOX_ENTRY_DELEGATION);

  const syncInboxEntryDelegation = useCallback(
    async (taskGroupId: string, entryId: string, delegationId?: string) => {
      const { data } = await syncInboxEntryDelegationMutation({
        variables: { taskGroupId, entryId, delegationId },
      });
      return data?.syncInboxEntryDelegation ?? null;
    },
    [syncInboxEntryDelegationMutation],
  );

  return { syncInboxEntryDelegation, loading, error };
}

interface SearchInboxEntriesParams {
  taskGroupId: string;
  keywords: string;
  // Opt-in: a caller that must tell "no match" from "search failed" gets the rejection.
  propagateError?: boolean;
}

interface SearchInboxEntriesResponseBody {
  message: {
    inboxEntries: InboxEntry[];
  };
}

let inboxEntriesServiceApi: ReturnType<typeof createApiWithAuth> | null = null;

const getInboxEntriesServiceApi = () => {
  if (!inboxEntriesServiceApi) {
    const baseUrl = import.meta.env.VITE_INBOX_ENTRIES_BASE_URL;
    if (!baseUrl) {
      throw new Error('Inbox Entries base URL is not set');
    }
    inboxEntriesServiceApi = createApiWithAuth(baseUrl);
  }
  return inboxEntriesServiceApi;
};

export function useSearchInboxEntries() {
  const searchInboxEntries = useCallback(
    async ({
      taskGroupId,
      keywords,
      propagateError = false,
    }: SearchInboxEntriesParams): Promise<InboxEntry[]> => {
      try {
        const response =
          await getInboxEntriesServiceApi().post<SearchInboxEntriesResponseBody>(
            '/inbox-entries/search',
            { taskGroupId, keywords },
          );
        return response.data.message.inboxEntries;
      } catch (error) {
        log.error('useSearchInboxEntries error:', error);
        if (propagateError) {
          throw error;
        }
        return [];
      }
    },
    [],
  );

  return { searchInboxEntries };
}
